import { useCallback, useEffect, useState } from 'react'
import { Search, CheckCircle, Send, XCircle } from 'lucide-react'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { EsqueletoTabla } from '@/shared/components/Esqueleto'
import { ModalDeAccionDeCierre } from '@/features/rh/components/ModalDeAccionDeCierre'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { Notification, useNotification } from '@/shared/components/Notification'
import {
  bandejaDeCierre,
  type CandidatoDeCierre,
  type EstatusDeCierre,
} from '@/features/rh/api/bandejaDeCierre'
import {
  PESTANAS,
  ACCIONES,
  sePuedeDecidir,
  accionesDeCierre,
  textoDeAprobacion,
  textoDeDecisionDeDireccion,
  textoDeDecisionFinal,
  mensajeDeFallo,
  type AccionDeCierre,
} from '@/features/rh/lib/bandejaDeCierre'
import { useEstadoEnUrl } from '@/shared/hooks/useEstadoEnUrl'

const PERMISO = 'rh.reclutamiento_candidatos.especial.cerrar_seleccion'

const ICONOS: Record<AccionDeCierre, React.ReactNode> = {
  seleccionar: <CheckCircle className="w-4 h-4" />,
  enviar_direccion: <Send className="w-4 h-4" />,
  descartar: <XCircle className="w-4 h-4" />,
}

/** Lo que se está confirmando: la acción y sobre quién. */
interface Pendiente {
  accion: AccionDeCierre
  candidato: CandidatoDeCierre
}

/**
 * Cierre y selección final de RH (SRH-304).
 *
 * El paso que antes no existía: la aprobación del solicitante dejó de cerrar
 * el proceso y ahora deja al candidato aquí, donde RH toma la decisión final.
 */
export const CierreDeSeleccion = () => {
  const { hasPermission, isLoading: cargandoPermisos } = usePermissions()
  const { notification, showSuccess, showError, hideNotification } =
    useNotification()

  const puedeCerrar = hasPermission(PERMISO)

  // La pestaña vive en la URL: volver de una ficha debe regresar a la misma.
  const [pestana, setPestana] = useEstadoEnUrl<EstatusDeCierre>(
    'tab',
    'pendiente_cierre',
  )

  const [candidatos, setCandidatos] = useState<CandidatoDeCierre[]>([])
  const [cargando, setCargando] = useState(true)
  const [errorDeCarga, setErrorDeCarga] = useState<string | null>(null)
  const [busqueda, setBusqueda] = useState('')
  const [pendiente, setPendiente] = useState<Pendiente | null>(null)
  /**
   * El fallo de carga va a un estado y no al aviso flotante.
   *
   * `showError` se recrea en cada render, así que meterlo aquí hacía que
   * `cargar` cambiara de identidad, el efecto volviera a correr y la pantalla
   * pidiera la lista en bucle hasta que el servidor cortaba por exceso.
   * Además un error de carga se lee mejor fijo que en un aviso que se va.
   */
  const cargar = useCallback(async (status: EstatusDeCierre) => {
    setCargando(true)
    setErrorDeCarga(null)
    try {
      setCandidatos(await bandejaDeCierre.listar(status))
    } catch (error: unknown) {
      setErrorDeCarga(mensajeDeFallo(error))
      setCandidatos([])
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    if (cargandoPermisos || !puedeCerrar) return
    void cargar(pestana)
  }, [pestana, cargar, cargandoPermisos, puedeCerrar])

  const cerrarModal = () => setPendiente(null)

  const alHacerse = () => {
    // Sale de esta pestaña en los tres casos, así que basta con quitarlo de la
    // lista: no hace falta recargar toda la pantalla.
    if (!pendiente) return
    const { candidato } = pendiente
    setCandidatos((previos) => previos.filter((c) => c.candidateId !== candidato.candidateId))
    cerrarModal()
    showSuccess('Listo', `${candidato.fullName} ya salió de esta bandeja.`)
  }

  const alFallar = (mensaje: string, esConflicto: boolean) => {
    cerrarModal()
    showError('No se pudo completar', mensaje)
    // En el 409 alguien más decidió: lo que se ve ya no es cierto.
    if (esConflicto) void cargar(pestana)
  }

  if (!cargandoPermisos && !puedeCerrar) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    )
  }

  const texto = busqueda.trim().toLowerCase()
  const visibles = candidatos.filter(
    (c) =>
      texto === '' ||
      [c.fullName, c.email, c.jobRequest?.position, c.jobRequest?.area]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(texto),
  )

  return (
    <div className="mx-auto px-6 py-8 max-w-7xl">
      <div className="mb-6">
        <TituloDePantalla>Cierre y selección final</TituloDePantalla>
        <p className="text-[#1E1E1F] text-sm">
          Candidatos que el solicitante aprobó y esperan la decisión de RH, y los que
          Dirección ya decidió y RH confirma.
        </p>
      </div>

      <nav
        className="flex flex-wrap gap-1 border-b border-[#E0E0E0] mb-6"
        aria-label="Secciones de la bandeja"
      >
        {PESTANAS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setPestana(tab.id)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm text-[#1E1E1F] transition-colors border-b-2 -mb-px ${
              pestana === tab.id
                ? 'border-[#477FFA] font-semibold'
                : 'border-transparent hover:border-gray-200'
            }`}
          >
            {tab.titulo}
          </button>
        ))}
      </nav>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-full max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar candidato..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-[#315FB9]"
          />
        </div>
        <p className="ml-auto flex items-center gap-2.5 text-sm text-gray-600 whitespace-nowrap">
          Candidatos
          <span className="inline-flex items-center justify-center rounded bg-[#EDFAFE] px-1.5 py-0.5 text-sm font-medium leading-6 text-[#315FB9] tabular-nums">
            {visibles.length.toLocaleString('es-MX')}
          </span>
        </p>
      </div>

      {errorDeCarga && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-sm text-red-700">{errorDeCarga}</p>
        </div>
      )}

      {cargando ? (
        <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
          <EsqueletoTabla filas={6} columnas={sePuedeDecidir(pestana) ? 6 : 5} />
        </div>
      ) : visibles.length === 0 ? (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
          <p className="text-gray-600">
            {busqueda
              ? 'Ningún candidato coincide con la búsqueda'
              : 'No hay candidatos en esta etapa'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  {['Candidato', 'Correo', 'Vacante', 'Área'].map((t) => (
                    <th
                      key={t}
                      className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                    >
                      {t}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    {pestana === 'seleccionado'
                      ? 'Decisión final'
                      : pestana === 'decision_direccion'
                        ? 'Decisión de Dirección'
                        : 'Aprobó'}
                  </th>
                  {sePuedeDecidir(pestana) && (
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {visibles.map((c) => (
                  <tr key={c.candidateId} className="hover:bg-[#F4F7FB] transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-[#1B2128]">
                      {c.fullName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {c.email || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#1B2128]">
                      {c.jobRequest?.position || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#1B2128]">
                      {c.jobRequest?.area || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {pestana === 'seleccionado' ? (
                        textoDeDecisionFinal(c.finalDecision)
                      ) : pestana === 'decision_direccion' ? (
                        <div>
                          <p
                            className={
                              c.directionDecision?.result === 'rejected'
                                ? 'font-medium text-[#B91C1C]'
                                : 'font-medium text-[#15803D]'
                            }
                          >
                            {textoDeDecisionDeDireccion(c.directionDecision)}
                          </p>
                          {c.directionDecision?.reason && (
                            <p className="mt-0.5">Motivo: {c.directionDecision.reason}</p>
                          )}
                          {c.directionDecision?.comment && (
                            <p className="mt-0.5 text-gray-500">{c.directionDecision.comment}</p>
                          )}
                        </div>
                      ) : (
                        textoDeAprobacion(c.requesterApproval)
                      )}
                    </td>
                    {sePuedeDecidir(pestana) && (
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                          {ACCIONES.filter((accion) =>
                            accionesDeCierre(pestana, c.directionDecision).includes(accion.id),
                          ).map((accion) => (
                            <button
                              key={accion.id}
                              type="button"
                              onClick={() => setPendiente({ accion: accion.id, candidato: c })}
                              className={`inline-flex items-center gap-1.5 text-sm font-medium hover:underline ${
                                accion.id === 'descartar'
                                  ? 'text-[#EF4444]'
                                  : 'text-[#315FB9]'
                              }`}
                            >
                              {ICONOS[accion.id]}
                              {accion.corta}
                            </button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pendiente && (
        <ModalDeAccionDeCierre
          accion={pendiente.accion}
          candidato={pendiente.candidato}
          onCerrar={cerrarModal}
          onHecho={alHacerse}
          onFallo={alFallar}
        />
      )}

      {notification?.isVisible && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          isVisible={notification.isVisible}
          onClose={hideNotification}
        />
      )}
    </div>
  )
}

export default CierreDeSeleccion
